<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import RealEstateCommissionInstallmentActions from '../components/RealEstateCommissionInstallmentActions.vue';
import RealEstateCommissionInstallmentBody from '../components/RealEstateCommissionInstallmentBody.vue';
import RealEstateCommissionSheetHeader from '../components/RealEstateCommissionSheetHeader.vue';
import { useRealEstateCommissionInstallment } from '../composables/useRealEstateCommissionInstallment';
import { useRealEstateCommissionSheetNavigation } from '../composables/useRealEstateCommissionSheetNavigation';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const installmentId = computed(() => route.params.installmentId);
const preview = computed(
  () => route.name === 'operacao_imobiliaria_apoio_parcela_visualizar'
);
const state = useRealEstateCommissionInstallment({
  autoStartRevision: computed(() => !preview.value),
  id: installmentId,
  t,
  notify: useAlert,
});
const paymentTarget = ref(null);
const sendDialogVisible = ref(false);
const paymentRow = computed(() => {
  const target = paymentTarget.value;
  return target ? state.form.value[target.group]?.[target.index] || null : null;
});
const paymentRecipientName = computed(
  () => paymentRow.value?.name || paymentRow.value?.nome_empresarial || ''
);
const goToPlan = () =>
  router.push({
    name: 'operacao_imobiliaria_apoio_planilha_comissao',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: route.params.salesTransactionId,
    },
  });
const goToMode = name => router.push({ name, params: route.params });
const navigation = useRealEstateCommissionSheetNavigation({
  onPreview: () => goToMode('operacao_imobiliaria_apoio_parcela_visualizar'),
  state,
  t,
});
const saveAndExit = async () => {
  if (await state.save()) goToPlan();
};
const selectRecipient = (group, index, optionId) => {
  const definition = navigation.groups.value.find(item => item.id === group);
  const option = definition?.options.find(
    item => String(item.id) === String(optionId)
  );
  state.setRecipient(group, index, option);
};
const openPayment = (group, index) => {
  paymentTarget.value = { group, index };
};
const savePayment = payment => {
  const target = paymentTarget.value;
  if (!target) return;
  state.updateRowFields(target.group, target.index, payment);
  paymentTarget.value = null;
};
const confirmSend = async () => {
  if (await state.send()) sendDialogVisible.value = false;
};
const editSheet = async () => {
  if (await state.startRevision()) {
    goToMode('operacao_imobiliaria_apoio_parcela');
  }
};
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-full bg-lais-bg-soft"
  >
    <RealEstateCommissionSheetHeader
      :can-edit="state.canEdit.value"
      :can-revise="state.canRevise.value"
      :preview="preview"
      :saving="state.saving.value || state.revising.value"
      @back="
        preview ? goToMode('operacao_imobiliaria_apoio_parcela') : goToPlan()
      "
      @edit="editSheet"
      @save-and-exit="saveAndExit"
    />
    <RealEstateCommissionInstallmentBody
      :active-group="navigation.activeGroup.value"
      :active-group-enabled="navigation.activeGroupEnabled.value"
      :active-group-error="navigation.activeGroupError.value"
      :active-group-quantity="navigation.activeGroupQuantity.value"
      :amount-cents="state.amountCents.value"
      :can-edit="state.canEdit.value"
      :menu-items="navigation.menuItems.value"
      :optionist-report="state.optionistReport.value"
      :optionist-report-exceeds="state.optionistReportExceeds.value"
      :optionist-report-types="state.optionistReportTypes.value"
      :preview="preview"
      :sheet-label="
        t('REAL_ESTATE_COMMISSION_INSTALLMENT.SHEET.LABEL', {
          number: state.installment.value?.sequence,
        })
      "
      :state="state"
      :title="navigation.groupTitle(navigation.activeGroup.value.id)"
      @add="state.addRow(navigation.activeGroup.value.id)"
      @payment="openPayment(navigation.activeGroup.value.id, $event)"
      @remove="state.removeRow(navigation.activeGroup.value.id, $event)"
      @select-group="navigation.selectGroup"
      @select-recipient="
        (index, optionId) =>
          selectRecipient(navigation.activeGroup.value.id, index, optionId)
      "
      @update="
        (index, field, value) =>
          state.updateRow(navigation.activeGroup.value.id, index, field, value)
      "
      @update-enabled="navigation.setGroupEnabled"
      @update-optionist-report="state.setOptionistReport"
      @update-quantity="
        state.setRowCount(navigation.activeGroup.value.id, $event)
      "
    />
    <RealEstateCommissionInstallmentActions
      :active-group-index="navigation.activeGroupIndex.value"
      :can-download="state.canDownload.value"
      :can-send="state.canSend.value"
      :downloading="state.downloading.value"
      :is-last-group="navigation.isLastGroup.value"
      :payment-recipient-name="paymentRecipientName"
      :payment-row="paymentRow"
      :payment-target="paymentTarget"
      :preview="preview"
      :send-dialog-visible="sendDialogVisible"
      :state="state"
      @close-payment="paymentTarget = null"
      @close-send="sendDialogVisible = false"
      @confirm-send="confirmSend"
      @download="state.download"
      @next="navigation.moveGroup(1)"
      @preview="navigation.previewSheet"
      @previous="navigation.moveGroup(-1)"
      @save-payment="savePayment"
      @send="sendDialogVisible = true"
    />
  </LaisGamificationSurface>
</template>
